import React, { useMemo, useRef } from 'react';

interface StableComputeStateFirstRender {
  value: null;
  firstRender: true;
}

interface StableComputeStateLaterRender<R> {
  value: R;
  firstRender: false;
}

type StableComputeState<R> = StableComputeStateFirstRender | StableComputeStateLaterRender<R>;

export function useStableComputedValue<R>(
  computeFn: () => R,
  isFetching: boolean,
  dependencies: React.DependencyList,
): R {
  const state = useRef<StableComputeState<R>>({ firstRender: true, value: null });

  // Compute the value only when isFetching is false
  // and dependencies change (tracked by useMemo)

  return useMemo<R>(() => {
    if (!isFetching || state.current.firstRender) {
      const result = computeFn();
      state.current.value = result;
      state.current.firstRender = false;
      return result;
    }

    return state.current.value;
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo -- dynamic dependency composition is required for this hook contract
  }, [isFetching, ...dependencies]);
}
